import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  CakeIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  StopIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import {
  getMealTypes,
  createMealType,
  updateMealType,
  deleteMealType,
  startMealType,
  stopMealType,
  getInstitutionsForMeals,
  type MealType,
  type Institution,
} from '@/api/mealsApi'
import { getGrades, getStreams, type Grade, type Stream } from '@/api/academicApi'
import toast from 'react-hot-toast'

const mealTypeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  defaultTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format (HH:MM)'),
  duration: z.number().min(15, 'Minimum 15 minutes').max(120, 'Maximum 120 minutes'),
  price: z.number().min(0, 'Price cannot be negative'),
  sortOrder: z.number().optional(),
  targetType: z.enum(['all', 'grades', 'streams']),
  targetGrades: z.array(z.string()).optional(),
  targetStreams: z.array(z.string()).optional(),
  institutionId: z.string().optional(),
})

type MealTypeFormData = z.infer<typeof mealTypeSchema>

const MealTypesPage: React.FC = () => {
  const { user } = useAuth()
  const [mealTypes, setMealTypes] = useState<MealType[]>([])
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [grades, setGrades] = useState<Grade[]>([])
  const [streams, setStreams] = useState<Stream[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedMealType, setSelectedMealType] = useState<MealType | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [actionInProgress, setActionInProgress] = useState<string | null>(null)

  const isSuperAdmin = user?.role === 'SUPER_ADMIN'

  const mealTypeForm = useForm<MealTypeFormData>({
    resolver: zodResolver(mealTypeSchema),
    defaultValues: {
      name: '',
      description: '',
      defaultTime: '12:00',
      duration: 30,
      price: 0,
      sortOrder: 0,
      targetType: 'all',
      targetGrades: [],
      targetStreams: [],
    },
  })

  const watchTargetType = mealTypeForm.watch('targetType')

  useEffect(() => {
    fetchMealTypes()
    fetchGradesAndStreams()
    if (isSuperAdmin) {
      fetchInstitutions()
    }
  }, [searchQuery])

  const fetchMealTypes = async () => {
    try {
      setLoading(true)
      const response = await getMealTypes({
        search: searchQuery || undefined,
      })
      setMealTypes(response.data)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load meal types')
    } finally {
      setLoading(false)
    }
  }

  const fetchInstitutions = async () => {
    try {
      const response = await getInstitutionsForMeals()
      setInstitutions(response.data)
    } catch (error: any) {
      console.error('Failed to load institutions:', error)
    }
  }

  const fetchGradesAndStreams = async () => {
    try {
      const [gradesResponse, streamsResponse] = await Promise.all([
        getGrades({ limit: 100 }),
        getStreams(),
      ])
      setGrades(gradesResponse.grades || [])
      setStreams(streamsResponse || [])
    } catch (error: any) {
      console.error('Failed to load grades/streams:', error)
    }
  }

  const handleAdd = async (data: MealTypeFormData) => {
    try {
      setSubmitting(true)
      await createMealType({
        ...data,
        targetGrades: data.targetType === 'grades' ? data.targetGrades : [],
        targetStreams: data.targetType === 'streams' ? data.targetStreams : [],
      })
      toast.success('Meal type created successfully')
      setShowAddModal(false)
      mealTypeForm.reset()
      fetchMealTypes()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create meal type')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = async (data: MealTypeFormData) => {
    if (!selectedMealType) return
    try {
      setSubmitting(true)
      await updateMealType(selectedMealType._id, {
        ...data,
        targetGrades: data.targetType === 'grades' ? data.targetGrades : [],
        targetStreams: data.targetType === 'streams' ? data.targetStreams : [],
      })
      toast.success('Meal type updated successfully')
      setShowEditModal(false)
      setSelectedMealType(null)
      mealTypeForm.reset()
      fetchMealTypes()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update meal type')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedMealType) return
    try {
      setSubmitting(true)
      await deleteMealType(selectedMealType._id)
      toast.success('Meal type deleted successfully')
      setShowDeleteModal(false)
      setSelectedMealType(null)
      fetchMealTypes()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete meal type')
    } finally {
      setSubmitting(false)
    }
  }

  const handleStartMeal = async (mealType: MealType) => {
    try {
      setActionInProgress(mealType._id)
      const response = await startMealType(mealType._id)
      toast.success(`${mealType.name} session started`)
      fetchMealTypes()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to start meal session')
    } finally {
      setActionInProgress(null)
    }
  }

  const handleStopMeal = async (mealType: MealType) => {
    try {
      setActionInProgress(mealType._id)
      await stopMealType(mealType._id)
      toast.success(`${mealType.name} session stopped`)
      fetchMealTypes()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to stop meal session')
    } finally {
      setActionInProgress(null)
    }
  }

  const openEditModal = (mealType: MealType) => {
    setSelectedMealType(mealType)
    mealTypeForm.reset({
      name: mealType.name,
      description: mealType.description || '',
      defaultTime: mealType.defaultTime,
      duration: mealType.duration,
      price: mealType.price,
      sortOrder: mealType.sortOrder,
      targetType: mealType.targetType || 'all',
      targetGrades: mealType.targetGrades || [],
      targetStreams: mealType.targetStreams || [],
    })
    setShowEditModal(true)
  }

  const openDeleteModal = (mealType: MealType) => {
    setSelectedMealType(mealType)
    setShowDeleteModal(true)
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const h = parseInt(hours)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const displayHour = h % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
    }).format(amount)
  }

  const getTargetLabel = (mealType: MealType) => {
    if (mealType.targetType === 'all') return 'All Students'
    if (mealType.targetType === 'grades') {
      const gradeNames = mealType.targetGrades
        ?.map(gId => grades.find(g => g._id === gId)?.name)
        .filter(Boolean)
        .join(', ')
      return gradeNames || 'Selected Grades'
    }
    if (mealType.targetType === 'streams') {
      const streamNames = mealType.targetStreams
        ?.map(sId => streams.find(s => s._id === sId)?.name)
        .filter(Boolean)
        .join(', ')
      return streamNames || 'Selected Streams'
    }
    return 'All Students'
  }

  const filteredMealTypes = mealTypes.filter(mt =>
    mt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    mt.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meal Types</h1>
          <p className="text-gray-600 mt-1">
            Manage meal types like Breakfast, Lunch, Snacks
          </p>
        </div>
        <Button onClick={() => {
          mealTypeForm.reset({
            name: '',
            description: '',
            defaultTime: '12:00',
            duration: 30,
            price: 0,
            sortOrder: 0,
            targetType: 'all',
            targetGrades: [],
            targetStreams: [],
          })
          setShowAddModal(true)
        }}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Meal Type
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Search meal types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Meal Types Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredMealTypes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <CakeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No meal types found</h3>
          <p className="text-gray-600 mb-4">
            {searchQuery ? 'Try a different search term' : 'Get started by adding your first meal type'}
          </p>
          {!searchQuery && (
            <Button onClick={() => setShowAddModal(true)}>
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Meal Type
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMealTypes.map((mealType) => (
            <motion.div
              key={mealType._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow ${
                mealType.isRunning ? 'border-green-300 ring-2 ring-green-100' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    mealType.isRunning ? 'bg-green-100' : 'bg-amber-100'
                  }`}>
                    <CakeIcon className={`h-6 w-6 ${
                      mealType.isRunning ? 'text-green-600' : 'text-amber-600'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{mealType.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        mealType.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {mealType.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {mealType.isRunning && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 animate-pulse">
                          In Session
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEditModal(mealType)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
                    disabled={mealType.isRunning}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openDeleteModal(mealType)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    disabled={mealType.isRunning}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {mealType.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {mealType.description}
                </p>
              )}

              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <ClockIcon className="h-4 w-4" />
                  <span>Default Time: {formatTime(mealType.defaultTime)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <ClockIcon className="h-4 w-4" />
                  <span>Duration: {mealType.duration} mins</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <CurrencyDollarIcon className="h-4 w-4" />
                  <span>Price: {formatCurrency(mealType.price)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <UserGroupIcon className="h-4 w-4" />
                  <span className="truncate">Target: {getTargetLabel(mealType)}</span>
                </div>
              </div>

              {/* Start/Stop Button */}
              <div className="pt-4 border-t border-gray-100">
                {mealType.isRunning ? (
                  <Button
                    variant="danger"
                    className="w-full"
                    onClick={() => handleStopMeal(mealType)}
                    disabled={actionInProgress === mealType._id}
                  >
                    {actionInProgress === mealType._id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Stopping...
                      </>
                    ) : (
                      <>
                        <StopIcon className="h-5 w-5 mr-2" />
                        Stop Session
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={() => handleStartMeal(mealType)}
                    disabled={!mealType.isActive || actionInProgress === mealType._id}
                  >
                    {actionInProgress === mealType._id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Starting...
                      </>
                    ) : (
                      <>
                        <PlayIcon className="h-5 w-5 mr-2" />
                        Start Session
                      </>
                    )}
                  </Button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {(showAddModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowAddModal(false)
              setShowEditModal(false)
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {showEditModal ? 'Edit Meal Type' : 'Add Meal Type'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowAddModal(false)
                      setShowEditModal(false)
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={mealTypeForm.handleSubmit(showEditModal ? handleEdit : handleAdd)} className="p-6 space-y-4">
                {isSuperAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Institution
                    </label>
                    <select
                      {...mealTypeForm.register('institutionId')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select Institution</option>
                      {institutions.map((inst) => (
                        <option key={inst._id} value={inst._id}>
                          {inst.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <Input
                    {...mealTypeForm.register('name')}
                    placeholder="e.g., Breakfast, Lunch, Snack"
                    error={mealTypeForm.formState.errors.name?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    {...mealTypeForm.register('description')}
                    placeholder="Optional description..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Time *
                    </label>
                    <Input
                      type="time"
                      {...mealTypeForm.register('defaultTime')}
                      error={mealTypeForm.formState.errors.defaultTime?.message}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (mins) *
                    </label>
                    <Input
                      type="number"
                      {...mealTypeForm.register('duration', { valueAsNumber: true })}
                      placeholder="30"
                      error={mealTypeForm.formState.errors.duration?.message}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price (KES)
                    </label>
                    <Input
                      type="number"
                      {...mealTypeForm.register('price', { valueAsNumber: true })}
                      placeholder="0"
                      error={mealTypeForm.formState.errors.price?.message}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sort Order
                    </label>
                    <Input
                      type="number"
                      {...mealTypeForm.register('sortOrder', { valueAsNumber: true })}
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Target Audience Section */}
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Target Audience</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Type
                    </label>
                    <select
                      {...mealTypeForm.register('targetType')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">All Students</option>
                      <option value="grades">Specific Grades</option>
                      <option value="streams">Specific Streams</option>
                    </select>
                  </div>

                  {watchTargetType === 'grades' && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Grades
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {grades.map((grade) => {
                          const selectedGrades = mealTypeForm.watch('targetGrades') || []
                          const isSelected = selectedGrades.includes(grade._id)
                          return (
                            <label
                              key={grade._id}
                              className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg cursor-pointer transition-colors ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const current = mealTypeForm.getValues('targetGrades') || []
                                  if (e.target.checked) {
                                    mealTypeForm.setValue('targetGrades', [...current, grade._id])
                                  } else {
                                    mealTypeForm.setValue('targetGrades', current.filter(id => id !== grade._id))
                                  }
                                }}
                                className="rounded text-primary-600"
                              />
                              <span className="text-sm">{grade.name}</span>
                            </label>
                          )
                        })}
                        {grades.length === 0 && (
                          <p className="text-sm text-gray-500">No grades available</p>
                        )}
                      </div>
                    </div>
                  )}

                  {watchTargetType === 'streams' && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Streams
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {streams.map((stream) => {
                          const selectedStreams = mealTypeForm.watch('targetStreams') || []
                          const isSelected = selectedStreams.includes(stream._id)
                          return (
                            <label
                              key={stream._id}
                              className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg cursor-pointer transition-colors ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const current = mealTypeForm.getValues('targetStreams') || []
                                  if (e.target.checked) {
                                    mealTypeForm.setValue('targetStreams', [...current, stream._id])
                                  } else {
                                    mealTypeForm.setValue('targetStreams', current.filter(id => id !== stream._id))
                                  }
                                }}
                                className="rounded text-primary-600"
                              />
                              <span className="text-sm">
                                {stream.gradeName && `${stream.gradeName} - `}{stream.name}
                              </span>
                            </label>
                          )
                        })}
                        {streams.length === 0 && (
                          <p className="text-sm text-gray-500">No streams available</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddModal(false)
                      setShowEditModal(false)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-5 w-5 mr-2" />
                        {showEditModal ? 'Update' : 'Create'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedMealType && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Meal Type</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                Are you sure you want to delete <strong>{selectedMealType.name}</strong>?
                This will fail if there are existing sessions using this meal type.
              </p>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  disabled={submitting}
                >
                  {submitting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default MealTypesPage
